/**
 * A read-only field that looks system-managed rather than broken.
 *
 * The Invoice form (OR-1449) pre-fills most of itself from the submission,
 * the broker and the policy, and the design marks every one of those with
 * a padlock and a muted fill: the value is real and correct, it just isn't
 * edited here. The card's own ⓘ tooltip says where it IS edited.
 *
 * Distinct from `isDisabled`, which renders at 50% opacity and reads as
 * "unavailable", and from a plain `isReadOnly` Input, which is the app's
 * existing convention (see `uw-information/sections/brokerage.tsx`) but
 * keeps a white background and offers no affordance explaining why typing
 * does nothing.
 */
import type { ReactNode } from "react";
import { Lock01 } from "@untitledui/icons";
import { Input, Label, cx } from "@tricura/atlas-ui";

interface LockedFieldProps {
    label: string;
    /** Rendered as the field's value. Empty falls back to `placeholder`. */
    value?: string | null;
    /** Shown when there is no value — "Automatically Populated" by default,
     *  matching the rest of the app's auto-filled fields. */
    placeholder?: string;
    size?: "sm" | "md";
    className?: string;
    /** A locked value can still be wrong for the form (e.g. a $0 premium
     *  the user must fix on another page). Flag it like any other field so
     *  the required-fields count never points at something invisible. */
    isInvalid?: boolean;
    /** Message under the field, shown through the Input's own hint slot. */
    hint?: ReactNode;
    "data-autotest"?: string;
}

/** The app's established muted treatment for an auto-filled value. */
const READONLY_INPUT_CLASS = "text-quaternary placeholder:text-quaternary pr-9";

export function LockedField({
    label,
    value,
    placeholder = "Automatically Populated",
    size = "sm",
    className,
    isInvalid,
    hint,
    "data-autotest": dataAutotest,
}: LockedFieldProps) {
    return (
        <div className={cx("flex w-full flex-col gap-1.5", className)}>
            <Label>{label}</Label>
            {/* The padlock is positioned over the field rather than passed as
              * a trailing addon so it shares the field's fill instead of
              * reading as a separate segment, which is how the Figma draws
              * it. `relative` wraps only the field — the label sits outside —
              * so vertical centring can't drift. */}
            <div className="relative">
                <Input
                    size={size}
                    isReadOnly
                    isInvalid={isInvalid}
                    hint={hint}
                    aria-label={label}
                    value={value ?? ""}
                    placeholder={placeholder}
                    inputClassName={READONLY_INPUT_CLASS}
                    // `!` is required: InputBase hard-codes `bg-primary` on the
                    // same wrapper, and two same-specificity Tailwind utilities
                    // resolve by stylesheet order, not by class order.
                    wrapperClassName="bg-secondary!"
                    data-autotest={dataAutotest}
                />
                {/* The Input places its own error icon in this slot when
                  * invalid; the padlock yields to it rather than overlap. */}
                {!isInvalid && (
                    <Lock01
                        aria-hidden="true"
                        className="text-fg-quaternary pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2"
                    />
                )}
            </div>
        </div>
    );
}
